import type { PipeTransform } from '@angular/core'
import { DOCUMENT, Pipe, inject } from '@angular/core'
import type { SafeHtml } from '@angular/platform-browser'
import { DomSanitizer } from '@angular/platform-browser'

import { TranslateService } from '@ngx-translate/core'

import type { SafeHtmlObject } from '../utils/html-utils'
import { escapeHtmlSpecialChars, isSafeHtmlObject } from '../utils/html-utils'

const ecliRegex = /\b(ECLI:[A-Z]{2}:[A-Z0-9]+:\d{4}:[A-Z0-9]+)\b/gi
const ecliUrl = 'https://deeplink.rechtspraak.nl/uitspraak?id='

@Pipe({
  name: 'processContent',
  pure: false,
})
/**
 * Pipe to process Den Hollander HTML content and convert it to appropriate
 * HTML.
 *
 * Features:
 *
 * - Converts ECLI references to clickable links with translated tooltips.
 * - Ensures external links open in a new tab with proper attributes.
 * - Assigns unique IDs to heading elements for anchor navigation.
 * - Removes empty paragraphs from legacy content.
 *
 * Usage: {{ content | htmlContent | processContent }}
 *
 * Note: This pipe is marked as impure (pure: false) to allow updates when the
 * input content changes.
 */
export class ProcessContentPipe implements PipeTransform {
  private sanitizer = inject(DomSanitizer)
  private document = inject<Document>(DOCUMENT)
  private translateService = inject(TranslateService)

  // Cache the last result so unchanged content doesn't produce a new SafeHtml
  // reference every change-detection cycle. A new reference forces Angular to
  // rewrite the bound innerHTML, which clears any active text selection.
  private lastContentString?: string
  private lastResult?: SafeHtml

  transform(content: SafeHtml): SafeHtml {
    if (!content) {
      return this.sanitizer.bypassSecurityTrustHtml('')
    }

    try {
      // Convert to string for processing
      const contentString: string = (content as SafeHtmlObject)
        .changingThisBreaksApplicationSecurity

      if (
        this.lastResult !== undefined &&
        contentString === this.lastContentString
      ) {
        return this.lastResult
      }

      // Import into DOM
      const tempDiv = this.document.createElement('div')
      tempDiv.innerHTML = contentString

      // Process external links first
      const links = tempDiv.getElementsByTagName('a')
      for (let i = 0; i < links.length; i++) {
        const link = links[i]
        if (!link.getAttribute('href')?.startsWith('#')) {
          link.setAttribute('target', '_blank')
          link.setAttribute('rel', 'noopener')
        }
      }

      // Add heading IDs
      const headings = tempDiv.querySelectorAll('h1, h2, h3, h4, h5, h6')
      for (let i = 0; i < headings.length; i++) {
        const heading = headings[i]
        heading.setAttribute('id', `heading-${i}`)
      }

      // Squash the HTML back to a string
      // TODO we should maybe do this in DOM as well
      let processedContent = tempDiv.innerHTML
        // Strip any empty paragraphs in old content, where double p was used instead of margin
        // It might contain whitespace or nbsp's
        .replace(/<p(?:[^>]*)>((?:\s|(?:&nbsp;))*?)<\/p>/gi, '')

      // Add ECLI links
      processedContent = processedContent.replace(
        ecliRegex,
        (match: string, ecli: string) => {
          const translatedLabel = this.translateService.instant(
            'pages.articles.ecliLinkTitle',
            { ecli: ecli }
          )
          const ecliLink = ecliUrl + encodeURIComponent(ecli)
          return `<a href="${escapeHtmlSpecialChars(ecliLink)}"
          title="${escapeHtmlSpecialChars(translatedLabel)}"
          target="_blank" rel="noopener" class="ecli">${escapeHtmlSpecialChars(ecli)}</a>`
        }
      )

      // Return as SafeHtml
      const result = this.sanitizer.bypassSecurityTrustHtml(processedContent)
      this.lastContentString = contentString
      this.lastResult = result
      return result
    } catch (error) {
      console.error('Error in processContent pipe:', error)
      return isSafeHtmlObject(content)
        ? content
        : this.sanitizer.bypassSecurityTrustHtml(String(content))
    }
  }
}
